import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Journey } from "../components/Journey";
import { CTABand } from "../components/CTABand";
import { useEnquiry } from "../components/EnquiryModal";
import { IMGS } from "../lib/img";
import { PenTool, Factory, Cog, Layers, Wrench, Hammer, ArrowRight, Quote } from "lucide-react";

const CAPABILITIES = [
  { icon: PenTool, name: "Design" },
  { icon: Factory, name: "Manufacturing" },
  { icon: Cog, name: "Engineering" },
  { icon: Layers, name: "Materials" },
  { icon: Wrench, name: "Hardware" },
  { icon: Hammer, name: "Installation" },
];

export default function About() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <SEO
        title="About Us — Built in Himachal, Designed for Modern India | Paramount Kitchen"
        description="Paramount Kitchen is a premium modular kitchen manufacturer based in Kangra District, Himachal Pradesh — combining design, manufacturing, engineering, materials, hardware and installation into complete kitchen solutions."
      />

      <section className="bg-royal pb-20 pt-36 text-ivory md:pb-28 md:pt-44">
        <div className="mx-auto grid max-w-7xl items-end gap-12 px-6 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow text-gold">About Paramount Kitchen</p>
            <h1 className="text-balance mt-5 font-display text-4xl font-medium leading-tight md:text-6xl">
              Built in <span className="serif-accent text-gold">Himachal.</span>
              <span className="block">Designed for Modern India.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ivory/70">
              Paramount Kitchen is based in Kangra District, Himachal Pradesh. We design,
              manufacture and install premium modular kitchens — with particular attention to the
              engineering behind the finish.
            </p>
            <button
              data-testid="about-design-my-kitchen"
              onClick={() => openEnquiry({ source: "about" })}
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-royal shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddim hover:text-ivory"
            >
              Design My Kitchen
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={IMGS.workshop}
              alt="Paramount Kitchen manufacturing workshop with plywood panels in Kangra, Himachal Pradesh"
              loading="lazy"
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <Quote className="mx-auto h-10 w-10 text-gold" strokeWidth={1.5} />
            <p className="text-balance mt-8 font-display text-3xl font-medium leading-snug text-ink md:text-5xl">
              &ldquo;Good kitchen design is not only about how a kitchen looks. It is about{" "}
              <span className="serif-accent text-golddim">how it performs every day.</span>&rdquo;
            </p>
            <p className="mt-8 text-sm font-bold uppercase tracking-[0.22em] text-ink/50">
              Our philosophy
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Complete Kitchen Solutions</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Six disciplines, <span className="serif-accent text-golddim">one kitchen.</span>
            </h2>
            <p className="mt-6 leading-relaxed text-ink/65">
              Paramount Kitchen combines six capabilities to create complete kitchen solutions —
              from the first sketch to the final installation.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`capability-${c.name.toLowerCase()}`}
                  className="group flex h-full flex-col items-center gap-4 rounded-2xl border border-ink/10 bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-sand text-wood transition-colors duration-300 group-hover:bg-gold group-hover:text-royal">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink/75">
                    {c.name}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Customer Journey</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Your journey <span className="serif-accent text-golddim">with us.</span>
            </h2>
          </Reveal>
          <div className="mt-14">
            <Journey />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
